import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { subscribeToMerch } from '../utils/emailService';

const MerchPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [notificationStatus, setNotificationStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNotificationStatus('');

    try {
      await subscribeToMerch(formData.name, formData.email);
      setNotificationStatus('✅ Success! You\'ll be notified when the merch store launches.');
      setFormData({ name: '', email: '' });
      setTimeout(() => setNotificationStatus(''), 5000);
    } catch (error) {
      console.error('Error submitting notification:', error);
      setNotificationStatus('❌ Error. Please try again.');
      setTimeout(() => setNotificationStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>Merch Store - Coming Soon | Petition.io</title>
        <meta name="description" content="The Petition.io merchandise store is coming soon. Support the movement with exclusive branded items." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center px-4 pt-20">
        <div className="max-w-4xl w-full text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block mb-6 sm:mb-8"
            >
              <span className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-blue/10 border border-primary-blue/20 rounded-full text-primary-blue text-xs sm:text-sm font-bold tracking-wider">
                MERCH STORE
              </span>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mb-8 sm:mb-12 mx-auto max-w-md md:max-w-lg"
            >
              <img 
                src="/images/merch.jpg" 
                alt="Petition.io Merchandise"
                className="w-full h-auto mx-auto rounded-xl sm:rounded-2xl shadow-2xl object-contain"
              />
            </motion.div>

            {/* Coming Soon Heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-neutral-dark mb-4 sm:mb-6">
                Coming Soon
              </h1>

              <p className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed">
                We're working on an exclusive collection of Petition.io merchandise. Support the movement and wear your values!
              </p>
            </motion.div>

            {/* Subscription Form */}
            <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                onSubmit={handleSubscribe}
                className="max-w-md mx-auto mb-8 px-4"
              >
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border border-gray-100">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-blue/10 rounded-full mb-3">
                      <svg className="w-6 h-6 text-primary-blue" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-neutral-dark mb-2 font-lato">Get Notified at Launch</h3>
                    <p className="text-sm text-gray-600 font-montserrat">
                      Be the first to know when our merch store goes live. Exclusive early access for subscribers!
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your Name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 transition-all outline-none text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 transition-all outline-none text-sm sm:text-base"
                      />
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-3 sm:py-4 bg-gradient-to-r from-primary-blue to-primary-light text-white text-base sm:text-lg font-bold font-montserrat rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Subscribing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                          </svg>
                          Notify Me
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
                
                {/* Status Message */}
                {notificationStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 text-center text-base sm:text-lg font-semibold px-4 py-3 bg-white rounded-xl shadow-lg"
                  >
                    {notificationStatus}
                  </motion.div>
                )}
              </motion.form>

              {/* Social Proof */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="flex items-center justify-center gap-4 text-gray-600 mb-8"
              >
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="font-medium">
                  100% of profits support petition causes
                </p>
              </motion.div>

              {/* Back to Home */}
              <Link to="/">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-primary-blue hover:text-primary-light transition-colors duration-300 flex items-center gap-2 mx-auto font-semibold"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Home
                </motion.button>
              </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MerchPage;
